// src/components/SuperAdminDashboard.jsx
import { useState, useEffect, useRef } from 'react';
import supabase from '../utils/supabaseClient';
import { sharedLibraryDb, jurisdictionDb, LEVEL_CHILDREN } from '../utils/store';
import { CA_AGENCIES } from '../utils/caAgencies';
import { REF_DOCS } from '../utils/constants';

// ── Module definitions ───────────────────────────────────────────────────────
const MODULE_DEFS = {
  // Contract tabs
  stage:            { label: 'Stage & Info',        icon: '◈', desc: 'Pipeline stage, DGS routing, contract details' },
  penny:            { label: 'Penny Drafting',       icon: '✦', desc: 'SOW, Exhibit B/D drafting, SCM compliance review' },
  assembly:         { label: 'Assembly',             icon: '⊞', desc: 'Exhibit management, export packages' },
  std213:           { label: 'STD 213',              icon: '📄', desc: 'Standard Agreement face sheet' },
  std215:           { label: 'STD 215',              icon: '📋', desc: 'Agreement Summary routing form' },
  bideval:          { label: 'Bid Evaluation',       icon: '📊', desc: 'Scoring criteria, bidder rankings' },
  handoff:          { label: 'Handoff & Closure',   icon: '📁', desc: 'Contract closeout and archive' },
  amendments:       { label: 'Amendments',           icon: '⊕', desc: 'Contract amendment tracking' },
  emails:           { label: 'Emails',               icon: '✉', desc: 'Procurement communications log' },
  vault:            { label: 'Vault',                icon: '⊘', desc: 'Secure document storage' },
  reminders:        { label: 'Reminders',            icon: '◎', desc: 'Deadline and action reminders' },
  sharing:          { label: 'Sharing / Collaboration', icon: '👥', desc: 'Share contracts with team members' },
  // Sidebar views
  analytics:        { label: 'Analytics',            icon: '◈', desc: 'Contract performance and metrics' },
  archive:          { label: 'Archive',              icon: '📁', desc: 'Closed/archived contracts' },
  library:          { label: 'Reference Library',   icon: '📚', desc: 'SCM and reference documents' },
  vendor_library:   { label: 'Vendor Library',       icon: '🏢', desc: 'Vendor database and history' },
  template_library: { label: 'Template Library',    icon: '📝', desc: 'Contract templates and samples' },
  paal:             { label: 'PAAL Management',      icon: '🔑', desc: 'Purchasing authority limits' },
  // CM Portal
  cm_module:        { label: 'CM Portal',            icon: '🏗', desc: 'Contract Manager role and dashboard' },
  sow_drafting:     { label: 'SOW Drafting (CM)',    icon: '✒', desc: 'CM SOW interview and drafting with Penny' },
};

const MODULE_GROUPS = {
  contract_tabs: {
    label: 'Contract Workspace Tabs',
    modules: ['stage', 'penny', 'assembly', 'std213', 'std215', 'bideval', 'handoff', 'amendments', 'emails', 'vault', 'reminders', 'sharing'],
  },
  sidebar_views: {
    label: 'Sidebar Navigation',
    modules: ['analytics', 'archive', 'library', 'vendor_library', 'template_library', 'paal'],
  },
  cm_portal: {
    label: 'Contract Manager Portal',
    modules: ['cm_module', 'sow_drafting'],
  },
};

const PRESET_CONFIGS = {
  starter: {
    label: '⚡ Starter',
    config: { stage: true, penny: true, std213: true, std215: true, assembly: true, bideval: false, handoff: false, amendments: false, emails: false, vault: false, reminders: true, sharing: false, analytics: false, archive: true, library: true, vendor_library: false, template_library: false, paal: true, cm_module: false, sow_drafting: false },
  },
  professional: {
    label: '🚀 Professional',
    config: { stage: true, penny: true, std213: true, std215: true, assembly: true, bideval: true, handoff: true, amendments: true, emails: true, vault: true, reminders: true, sharing: true, analytics: true, archive: true, library: true, vendor_library: true, template_library: true, paal: true, cm_module: false, sow_drafting: false },
  },
  cm_only: {
    label: '🏗 CM Only',
    config: { stage: false, penny: false, std213: false, std215: false, assembly: false, bideval: false, handoff: false, amendments: false, emails: false, vault: false, reminders: false, sharing: false, analytics: false, archive: false, library: false, vendor_library: false, template_library: false, paal: false, cm_module: true, sow_drafting: true },
  },
};

function getDefaultModuleConfig() {
  return Object.fromEntries(Object.keys(MODULE_DEFS).map(k => [k, true]));
}

export default function SuperAdminDashboard({ onEnterApp }) {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('pending');
  const [working, setWorking]   = useState({});
  const [activeTab, setActiveTab] = useState('users');
  // Tab keys (internal) vs labels (displayed):
  //   'users'        → "User Approvals"
  //   'jurisdictions'→ "Jurisdictions"
  //   'library'      → "Shared Library"
  //   'feedback'     → "Feedback"
  //   'agencies'     → "Signup Registry"  (the static CA_AGENCIES list)
  //   'modules'      → "Agencies"         (real registered agencies — jurisdiction/modules/doc profile)
  //   'ai_usage'     → "AI Usage"
  // Keys kept as-is to avoid touching all the activeTab=== checks; only labels were swapped.
  const [feedback, setFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState('all'); // all | 1 | 2 | 3 | 4 | 5
  const [customAgencies, setCustomAgencies] = useState([]);
  const [newAgencyName, setNewAgencyName] = useState('');
  const [newAgencyCode, setNewAgencyCode] = useState('');
  const [newAgencyAbbr, setNewAgencyAbbr] = useState('');
  const [newAgencyCategory, setNewAgencyCategory] = useState('');
  const [sharedLib, setSharedLib] = useState({});
  const [libLoading, setLibLoading] = useState(false);
  const fileRefs = useRef({});
  // Jurisdiction tree — loaded for the library tab (doc scope pickers) and
  // the agencies tab (agency jurisdiction pickers). Flat array of nodes;
  // each has { id, parent_id, level, name, path }.
  const [jurisdictionNodes, setJurisdictionNodes] = useState([]);
  const [jurisdictionLoading, setJurisdictionLoading] = useState(false);
  // Tree-management tab UI state:
  //   jAddingUnder  — node id we're adding a child under (null = not adding)
  //   jNewName      — name field for the new child
  //   jNewLevel     — level field for the new child
  //   jRenaming     — node id being renamed inline (null = none)
  //   jRenameName   — rename field value
  //   jBusy         — true while a create/rename/delete is in flight
  const [jAddingUnder, setJAddingUnder] = useState(null);
  const [jNewName, setJNewName] = useState('');
  const [jNewLevel, setJNewLevel] = useState('');
  const [jRenaming, setJRenaming] = useState(null);
  const [jRenameName, setJRenameName] = useState('');
  const [jBusy, setJBusy] = useState(false);
  const [registeredAgencies, setRegisteredAgencies] = useState([]);
  const [agenciesLoading, setAgenciesLoading] = useState(false);
  const [editingModules, setEditingModules] = useState(null); // agency_id being edited
  const [moduleConfig, setModuleConfig] = useState({});
  const [savingModules, setSavingModules] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(null); // agency_id uploading logo
  const logoRefs = useRef({});

  // Doc-template profile editor state
  const [editingDocTemplate, setEditingDocTemplate] = useState(null); // agency_id being edited
  const [docTemplateDraft, setDocTemplateDraft] = useState(''); // raw JSON text in textarea
  const [docTemplateError, setDocTemplateError] = useState('');
  const [savingDocTemplate, setSavingDocTemplate] = useState(false);

  // AI usage tracking state
  const [aiUsage, setAiUsage] = useState([]);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);
  const [aiUsageDays, setAiUsageDays] = useState(7); // 7 | 30

  useEffect(() => { loadUsers(); }, [filter]);
  useEffect(() => { loadRegisteredAgencies(); }, []); // needed for the agency-reassignment dropdown on the Users tab, not just the Agencies tab
  useEffect(() => { if (activeTab === 'agencies') { loadRegisteredAgencies(); loadJurisdictionTree(); } }, [activeTab]);
  useEffect(() => { if (activeTab === 'jurisdictions') loadJurisdictionTree(); }, [activeTab]); // eslint-disable-line
  useEffect(() => { if (activeTab === 'ai_usage') loadAiUsage(); }, [activeTab, aiUsageDays]); // eslint-disable-line

  async function loadAiUsage() {
    setAiUsageLoading(true);
    const since = new Date(Date.now() - aiUsageDays * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('ai_usage_log')
      .select('tag, model, input_tokens, output_tokens, cache_read_tokens, cache_creation_tokens, cost_usd, user_id, contract_id, error, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(5000); // hard cap so this doesn't blow up on heavy months
    if (!error && data) setAiUsage(data);
    setAiUsageLoading(false);
  }

  async function loadRegisteredAgencies() {
    setAgenciesLoading(true);
    const { data, error } = await supabase
      .from('agencies')
      .select('id, name, division, module_config, logo_url, doc_template, jurisdiction_node_id')
      .order('name');
    if (error) console.error('loadRegisteredAgencies:', error);
    if (!error) setRegisteredAgencies(data || []);
    setAgenciesLoading(false);
  }

  // Change which jurisdiction node an agency sits under. This determines
  // which shared-library documents the agency's users can see.
  async function changeAgencyJurisdiction(agencyId, nodeId) {
    if (!nodeId) return;
    const ok = await jurisdictionDb.setAgencyNode(agencyId, nodeId);
    if (ok) {
      setRegisteredAgencies(prev => prev.map(a =>
        a.id === agencyId ? { ...a, jurisdiction_node_id: nodeId } : a
      ));
    } else {
      alert('Failed to update agency jurisdiction — check console for details');
    }
  }

  // ── Doc-template profile editor ──
  function openDocTemplateEditor(agency) {
    setEditingDocTemplate(agency.id);
    // Pretty-print existing JSON, or seed with an empty placeholder
    const existing = agency.doc_template;
    setDocTemplateDraft(existing ? JSON.stringify(existing, null, 2) : '{\n  \n}');
    setDocTemplateError('');
  }

  function closeDocTemplateEditor() {
    setEditingDocTemplate(null);
    setDocTemplateDraft('');
    setDocTemplateError('');
  }

  async function saveDocTemplate(agencyId) {
    setSavingDocTemplate(true);
    setDocTemplateError('');
    let parsed;
    try {
      parsed = docTemplateDraft.trim() ? JSON.parse(docTemplateDraft) : null;
    } catch (e) {
      setDocTemplateError('Invalid JSON: ' + e.message);
      setSavingDocTemplate(false);
      return;
    }
    const { error } = await supabase.from('agencies')
      .update({ doc_template: parsed }).eq('id', agencyId);
    setSavingDocTemplate(false);
    if (error) {
      setDocTemplateError('Save failed: ' + error.message);
    } else {
      setRegisteredAgencies(prev => prev.map(a => a.id === agencyId ? { ...a, doc_template: parsed } : a));
      closeDocTemplateEditor();
    }
  }

  function clearDocTemplate(agencyId) {
    if (!window.confirm('Clear this agency\'s doc template profile? They will fall back to the app default.')) return;
    setDocTemplateDraft('');
    saveDocTemplate(agencyId);
  }

  async function saveModuleConfig(agencyId) {
    setSavingModules(true);
    const { error } = await supabase
      .from('agencies')
      .update({ module_config: moduleConfig })
      .eq('id', agencyId);
    setSavingModules(false);
    if (!error) {
      setRegisteredAgencies(prev => prev.map(a => a.id === agencyId ? { ...a, module_config: moduleConfig } : a));
      setEditingModules(null);
    } else {
      alert('Error saving: ' + error.message);
    }
  }

  async function uploadAgencyLogo(agencyId, file) {
    setUploadingLogo(agencyId);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result; // full data URL
        const { error } = await supabase
          .from('agencies')
          .update({ logo_url: base64 })
          .eq('id', agencyId);
        if (!error) {
          setRegisteredAgencies(prev => prev.map(a => a.id === agencyId ? { ...a, logo_url: base64 } : a));
        } else {
          alert('Upload failed: ' + error.message);
        }
        setUploadingLogo(null);
      };
      reader.readAsDataURL(file);
    } catch (e) {
      alert('Error: ' + e.message);
      setUploadingLogo(null);
    }
  }

  async function removeAgencyLogo(agencyId) {
    const { error } = await supabase.from('agencies').update({ logo_url: null }).eq('id', agencyId);
    if (!error) setRegisteredAgencies(prev => prev.map(a => a.id === agencyId ? { ...a, logo_url: null } : a));
  }

  async function loadUsers() {
    setLoading(true);
    // Use rpc to bypass RLS for super admin queries
    const { data, error } = await supabase
      .rpc('get_users_by_status', { p_status: filter });
    if (error) {
      console.error('loadUsers error:', error);
      // Fallback: try direct query
      const { data: d2, error: e2 } = await supabase
        .from('user_profiles')
        .select('id, contact_name, contact_email, agency_id, agency_name, agency_division, role, status, created_at, last_seen_at')
        .eq('status', filter)
        .order('created_at', { ascending: false });
      if (!e2) setUsers(d2 || []);
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  }

  async function loadSharedLib() {
    setLibLoading(true);
    const keys = await sharedLibraryDb.list();
    const obj = {};
    keys.forEach(k => {
      obj[k.key] = {
        name: k.name, size: k.file_size, uploaded_at: k.uploaded_at,
        scope_node_id: k.scope_node_id || null,
      };
    });
    setSharedLib(obj);
    setLibLoading(false);
  }

  // Load the jurisdiction tree. Used by both the library tab (doc scope
  // pickers) and the agencies tab (agency jurisdiction pickers).
  async function loadJurisdictionTree() {
    setJurisdictionLoading(true);
    const nodes = await jurisdictionDb.listNodes();
    setJurisdictionNodes(nodes);
    setJurisdictionLoading(false);
  }

  // Build an indented label for a node, e.g. "    California State Government",
  // so a flat <select> still reads as a hierarchy. Depth = path length - 1.
  function nodeIndentLabel(node) {
    const depth = Math.max(0, (node.path || []).length - 1);
    return '\u00A0\u00A0'.repeat(depth) + node.name;
  }

  // ── Jurisdiction tree management (Jurisdictions tab) ──────────────────
  // Begin adding a child under `parentNode`. Pre-selects the first valid
  // child level for that parent's level (per LEVEL_CHILDREN).
  function startAddChild(parentNode) {
    const validLevels = LEVEL_CHILDREN[parentNode.level] || [];
    setJAddingUnder(parentNode.id);
    setJNewName('');
    setJNewLevel(validLevels[0] || '');
    setJRenaming(null); // close any open rename
  }

  function cancelAddChild() {
    setJAddingUnder(null);
    setJNewName('');
    setJNewLevel('');
  }

  async function submitAddChild(parentNode) {
    if (!jNewName.trim() || !jNewLevel) return;
    setJBusy(true);
    try {
      const res = await jurisdictionDb.createNode({
        parent_id: parentNode.id,
        level: jNewLevel,
        name: jNewName.trim(),
      });
      if (res.ok) {
        cancelAddChild();
        await loadJurisdictionTree();
      } else if (res.reason === 'duplicate_name') {
        alert('A node named "' + jNewName.trim() + '" already exists under ' + parentNode.name + '.');
      } else {
        alert('Could not create node: ' + (res.reason || 'unknown error'));
      }
    } finally {
      setJBusy(false);
    }
  }

  function startRename(node) {
    setJRenaming(node.id);
    setJRenameName(node.name);
    setJAddingUnder(null); // close any open add form
  }

  function cancelRename() {
    setJRenaming(null);
    setJRenameName('');
  }

  async function submitRename(node) {
    const newName = jRenameName.trim();
    if (!newName || newName === node.name) { cancelRename(); return; }
    setJBusy(true);
    try {
      const res = await jurisdictionDb.renameNode(node.id, newName);
      if (res.ok) {
        cancelRename();
        await loadJurisdictionTree();
      } else if (res.reason === 'duplicate_name') {
        alert('A sibling node named "' + newName + '" already exists.');
      } else {
        alert('Could not rename node: ' + (res.reason || 'unknown error'));
      }
    } finally {
      setJBusy(false);
    }
  }

  async function deleteNode(node) {
    if (!window.confirm('Delete the jurisdiction node "' + node.name + '"? This cannot be undone.')) return;
    setJBusy(true);
    try {
      const res = await jurisdictionDb.deleteNode(node.id);
      if (res.ok) {
        await loadJurisdictionTree();
      } else if (res.reason === 'has_dependents') {
        const parts = [];
        if (res.childCount > 0)  parts.push(res.childCount + ' child node' + (res.childCount === 1 ? '' : 's'));
        if (res.agencyCount > 0) parts.push(res.agencyCount + ' agenc' + (res.agencyCount === 1 ? 'y' : 'ies'));
        if (res.docCount > 0)    parts.push(res.docCount + ' document' + (res.docCount === 1 ? '' : 's'));
        alert(
          'Cannot delete "' + node.name + '" — it still has ' + parts.join(', ') + ' attached.\n\n' +
          'Reassign those to another jurisdiction first, then delete this node.'
        );
      } else if (res.reason === 'cannot_delete_root') {
        alert('The root jurisdiction cannot be deleted.');
      } else {
        alert('Could not delete node: ' + (res.reason || 'unknown error'));
      }
    } finally {
      setJBusy(false);
    }
  }

  // Change a shared-library document's jurisdiction scope.
  async function changeDocScope(key, nodeId) {
    if (!nodeId) return;
    const ok = await sharedLibraryDb.setScope(key, nodeId);
    if (ok) {
      setSharedLib(p => ({ ...p, [key]: { ...p[key], scope_node_id: nodeId } }));
    } else {
      alert('Failed to update scope — check console for details');
    }
  }

  async function uploadSharedDoc(key, file) {
    setLibLoading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(file);
      });
      // Preserve the doc's existing scope on replace; if it had none yet
      // (fresh upload), default to the root node so it's visible to everyone
      // until the admin narrows it. Root is the node with parent_id === null.
      const existingScope = sharedLib[key]?.scope_node_id;
      const rootNode = jurisdictionNodes.find(n => !n.parent_id);
      const scopeToUse = existingScope || rootNode?.id || null;
      const ok = await sharedLibraryDb.set(key, { base64: b64, name: file.name, size: file.size }, scopeToUse);
      if (ok) {
        setSharedLib(p => ({ ...p, [key]: {
          name: file.name, size: file.size, uploaded_at: new Date().toISOString(),
          scope_node_id: scopeToUse,
        } }));
        alert('✓ ' + file.name + ' uploaded successfully');
      } else {
        alert('Upload failed — check console for details');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setLibLoading(false);
    }
  }

  async function removeSharedDoc(key) {
    await sharedLibraryDb.del(key);
    setSharedLib(p => { const n = { ...p }; delete n[key]; return n; });
  }

  useEffect(() => { if (activeTab === 'library') { loadSharedLib(); loadJurisdictionTree(); } }, [activeTab]);
  useEffect(() => { if (activeTab === 'feedback') loadFeedback(); }, [activeTab]); // eslint-disable-line

  async function loadFeedback() {
    setFeedbackLoading(true);
    const { data, error } = await supabase
      .from('app_feedback')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error) setFeedback(data || []);
    setFeedbackLoading(false);
  }

  async function updateStatus(userId, status) {
    setWorking(w => ({ ...w, [userId]: true }));
    const { error } = await supabase.rpc('update_user_status', {
      p_user_id: userId,
      p_status: status,
    });
    if (!error) {
      // Remove from current list immediately
      setUsers(u => u.filter(x => x.id !== userId));
    } else {
      console.error('updateStatus error:', error);
    }
    setWorking(w => ({ ...w, [userId]: false }));
  }

  async function reassignAgency(userId, agencyId) {
    if (!agencyId) return;
    setWorking(w => ({ ...w, [userId]: true }));
    const { error } = await supabase.rpc('reassign_user_agency', {
      p_user_id: userId,
      p_agency_id: agencyId,
    });
    if (!error) {
      const agency = registeredAgencies.find(a => a.id === agencyId);
      setUsers(u => u.map(x => x.id === userId
        ? { ...x, agency_id: agencyId, agency_name: agency?.name ?? x.agency_name, agency_division: agency?.division ?? x.agency_division }
        : x));
    } else {
      console.error('reassignAgency error:', error);
    }
    setWorking(w => ({ ...w, [userId]: false }));
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  const counts = { pending: 0, active: 0, rejected: 0 };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f1f3d 0%, #1a3260 60%, #0f1f3d 100%)',
      fontFamily: 'Inter, system-ui, sans-serif', padding: 32,
    }}>
      {/* Header */}
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 900, color: '#0f1f3d',
            }}>GS</div>
            <div>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 800 }}>GoldenScope</div>
              <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 12 }}>Super Admin — User Approvals</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {onEnterApp && (
              <button onClick={onEnterApp} style={{
                background: 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)',
                border: 'none', borderRadius: 8,
                color: '#0f1f3d', cursor: 'pointer',
                padding: '8px 16px', fontSize: 13, fontWeight: 700,
              }}>
                ⊕ Enter GoldenScope
              </button>
            )}
            <button onClick={handleSignOut} style={{
              background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)',
              borderRadius: 8, color: 'rgba(255,255,255,.6)', cursor: 'pointer',
              padding: '8px 16px', fontSize: 13,
            }}>
              ⎋ Sign Out
            </button>
          </div>
        </div>

        {/* Main tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { key: 'users',   label: '👥 User Approvals' },
            { key: 'jurisdictions', label: '🗺 Jurisdictions' },
            { key: 'library', label: '📚 Shared Library' },
            { key: 'feedback', label: '💬 Feedback' },
            { key: 'agencies', label: '📇 Signup Registry' },
            { key: 'modules', label: '🏛 Agencies' },
            { key: 'ai_usage', label: '✦ AI Usage' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setActiveTab(key)} style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: activeTab === key
                ? 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)'
                : 'rgba(255,255,255,.08)',
              color: activeTab === key ? '#0f1f3d' : 'rgba(255,255,255,.6)',
              fontWeight: activeTab === key ? 700 : 400,
              cursor: 'pointer', fontSize: 13,
            }}>{label}</button>
          ))}
        </div>

        {/* User filter sub-tabs */}
        {activeTab === 'users' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[
              { key: 'pending',  label: '⏳ Pending' },
              { key: 'active',   label: '✓ Approved' },
              { key: 'rejected', label: '⛔ Rejected' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setFilter(key)} style={{
                padding: '6px 14px', borderRadius: 8, border: 'none',
                background: filter === key ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.06)',
                color: filter === key ? '#fff' : 'rgba(255,255,255,.5)',
                fontWeight: filter === key ? 600 : 400,
                cursor: 'pointer', fontSize: 12,
              }}>{label}</button>
            ))}
          </div>
        )}

        {/* Jurisdictions panel — manage the jurisdiction tree */}
        {activeTab === 'jurisdictions' && (
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,.4)', overflow: 'hidden', padding: 28 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#0f1f3d' }}>
              Jurisdiction Hierarchy
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              The jurisdiction tree controls which shared-library documents each agency can see.
              A document scoped to a node is visible to every agency at that node <strong>or below it</strong>.
              Add a state, then jurisdictions or localities (counties, cities) beneath it as new
              jurisdictions onboard.
            </p>

            {jurisdictionLoading ? (
              <div style={{ color: '#94a3b8', fontSize: 14, padding: '20px 0' }}>Loading…</div>
            ) : jurisdictionNodes.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 14, padding: '20px 0' }}>
                No jurisdiction nodes found. (The Stage 1 migration should have seeded a root —
                if you see this, something is wrong.)
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {jurisdictionNodes.map(node => {
                  const depth = Math.max(0, (node.path || []).length - 1);
                  const isRoot = !node.parent_id;
                  const validChildLevels = LEVEL_CHILDREN[node.level] || [];
                  const canAddChild = validChildLevels.length > 0;
                  const isRenaming = jRenaming === node.id;
                  const isAddingHere = jAddingUnder === node.id;
                  return (
                    <div key={node.id}>
                      {/* Node row */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 8,
                        border: '1px solid #e2e8f0', background: isRoot ? '#fffbeb' : '#fafafa',
                        marginLeft: depth * 28,
                      }}>
                        {/* Level badge */}
                        <span style={{
                          fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '.04em', padding: '2px 7px', borderRadius: 5,
                          background: isRoot ? '#fde68a' : '#e0e7ff',
                          color: isRoot ? '#854d0e' : '#3730a3', flexShrink: 0,
                        }}>{node.level}</span>

                        {/* Name (or rename field) */}
                        {isRenaming ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                            <input
                              type="text" value={jRenameName}
                              onChange={e => setJRenameName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') submitRename(node); if (e.key === 'Escape') cancelRename(); }}
                              autoFocus
                              style={{ flex: 1, fontSize: 13, padding: '5px 9px', borderRadius: 6, border: '1.5px solid #60a5fa' }}
                            />
                            <button onClick={() => submitRename(node)} disabled={jBusy}
                              style={{ padding: '5px 11px', borderRadius: 6, border: 'none', background: '#0f1f3d', color: '#fff', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                            <button onClick={cancelRename} disabled={jBusy}
                              style={{ padding: '5px 11px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 11.5, cursor: 'pointer', color: '#64748b' }}>Cancel</button>
                          </div>
                        ) : (
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#0f1f3d' }}>{node.name}</span>
                        )}

                        {/* Actions */}
                        {!isRenaming && (
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            {canAddChild && (
                              <button onClick={() => startAddChild(node)} disabled={jBusy}
                                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#0f1f3d' }}>
                                + Add child
                              </button>
                            )}
                            <button onClick={() => startRename(node)} disabled={jBusy}
                              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#64748b' }}>
                              Rename
                            </button>
                            {!isRoot && (
                              <button onClick={() => deleteNode(node)} disabled={jBusy}
                                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', fontSize: 11, cursor: 'pointer', color: '#dc2626' }}>
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Inline "add child" form */}
                      {isAddingHere && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '10px 14px', marginLeft: (depth + 1) * 28, marginTop: 4,
                          borderRadius: 8, border: '1.5px dashed #cbd5e1', background: '#f8fafc',
                        }}>
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b' }}>New child:</span>
                          <input
                            type="text" value={jNewName}
                            onChange={e => setJNewName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') submitAddChild(node); if (e.key === 'Escape') cancelAddChild(); }}
                            placeholder="Name (e.g. County of Los Angeles)"
                            autoFocus
                            style={{ flex: 1, fontSize: 12.5, padding: '5px 9px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                          <select value={jNewLevel} onChange={e => setJNewLevel(e.target.value)}
                            style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                            {validChildLevels.map(lvl => (
                              <option key={lvl} value={lvl}>{lvl}</option>
                            ))}
                          </select>
                          <button onClick={() => submitAddChild(node)} disabled={jBusy || !jNewName.trim() || !jNewLevel}
                            style={{ padding: '5px 12px', borderRadius: 6, border: 'none',
                              background: (jBusy || !jNewName.trim() || !jNewLevel) ? '#cbd5e1' : 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)',
                              color: '#0f1f3d', fontSize: 11.5, fontWeight: 700, cursor: (jBusy || !jNewName.trim()) ? 'not-allowed' : 'pointer' }}>
                            Add
                          </button>
                          <button onClick={cancelAddChild} disabled={jBusy}
                            style={{ padding: '5px 11px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 11.5, cursor: 'pointer', color: '#64748b' }}>
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Shared Library panel */}
        {activeTab === 'library' && (
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,.4)', overflow: 'hidden', padding: 28 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#0f1f3d' }}>
              Shared Reference Library
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              Documents uploaded here are shared across agencies. Each document is scoped to a
              <strong> jurisdiction</strong> — it's visible to every agency at that jurisdiction or below it
              in the hierarchy. For example, a document scoped to "California State Government" is seen by all
              California state agencies, but not by other states or by California cities and counties.
            </p>
            {libLoading ? (
              <div style={{ color: '#94a3b8', fontSize: 14, padding: '20px 0' }}>Loading…</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {REF_DOCS.map(doc => {
                  const loaded = sharedLib[doc.key];
                  return (
                    <div key={doc.key} style={{
                      display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px',
                      borderRadius: 10, border: '1.5px solid ' + (loaded ? '#bbf7d0' : '#e2e8f0'),
                      background: loaded ? '#f0fdf4' : '#fafafa',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f1f3d' }}>{doc.label}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{doc.desc}</div>
                        {loaded && (
                          <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>
                            ✓ {loaded.name} · {loaded.uploaded_at ? new Date(loaded.uploaded_at).toLocaleDateString() : ''}
                          </div>
                        )}
                        {loaded && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Visible to:</span>
                            {jurisdictionLoading ? (
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>loading jurisdictions…</span>
                            ) : (
                              <select
                                value={loaded.scope_node_id || ''}
                                onChange={e => changeDocScope(doc.key, e.target.value)}
                                style={{
                                  fontSize: 11.5, padding: '4px 8px', borderRadius: 6,
                                  border: '1px solid #cbd5e1', background: '#fff', color: '#0f1f3d',
                                  cursor: 'pointer', maxWidth: 320,
                                }}
                              >
                                {!loaded.scope_node_id && <option value="">— not scoped —</option>}
                                {jurisdictionNodes.map(n => (
                                  <option key={n.id} value={n.id}>{nodeIndentLabel(n)}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <input
                          ref={el => { if (el) fileRefs.current[doc.key] = el; }}
                          type="file" accept=".pdf" style={{ display: 'none' }}
                          onChange={async e => {
                            if (e.target.files[0]) {
                              await uploadSharedDoc(doc.key, e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                        />
                        <button
                          onClick={() => fileRefs.current[doc.key]?.click()}
                          style={{
                            padding: '7px 14px', borderRadius: 7, border: 'none',
                            background: loaded ? '#0f1f3d' : 'linear-gradient(135deg, #c49922 0%, #e8c04a 100%)',
                            color: loaded ? '#fff' : '#0f1f3d',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          {loaded ? 'Replace' : '↑ Upload'}
                        </button>
                        {loaded && (
                          <button
                            onClick={() => removeSharedDoc(doc.key)}
                            style={{
                              padding: '7px 14px', borderRadius: 7, border: 'none',
                              background: '#fee2e2', color: '#dc2626',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* User list */}
        {activeTab === 'users' && <div style={{
          background: '#fff', borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,.4)', overflow: 'hidden',
        }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              Loading…
            </div>
          ) : users.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              No {filter} users
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                  {['Name', 'Email', 'Agency', 'Signed Up', 'Last Seen', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '14px 20px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '.06em', color: '#94a3b8',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} style={{
                    borderBottom: i < users.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: i % 2 === 0 ? '#fff' : '#fafafa',
                  }}>
                    <td style={{ padding: '14px 20px', fontWeight: 600, fontSize: 14, color: '#0f1f3d' }}>
                      {u.contact_name || '—'}
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: 13, color: '#475569', fontFamily: 'monospace' }}>
                      {u.contact_email || '—'}
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: 13, color: '#475569' }}>
                      <select
                        value={u.agency_id || ''}
                        disabled={working[u.id]}
                        onChange={e => reassignAgency(u.id, e.target.value)}
                        style={{
                          fontSize: 12.5, padding: '5px 8px', borderRadius: 6,
                          border: '1px solid #e2e8f0', color: '#0f1f3d',
                          background: '#fff', maxWidth: 180, cursor: 'pointer',
                        }}
                      >
                        <option value="" disabled>
                          {u.agency_id ? 'Select agency…' : (u.agency_name ? 'Unassigned — signed up as "' + u.agency_name + '"' : 'Unassigned')}
                        </option>
                        {registeredAgencies.map(a => (
                          <option key={a.id} value={a.id}>{a.name}{a.division ? ' — ' + a.division : ''}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: 12, color: u.last_seen_at ? '#475569' : '#cbd5e1', fontFamily: 'monospace' }}>
                      {u.last_seen_at ? (() => {
                        const d = new Date(u.last_seen_at);
                        const now = Date.now();
                        const diffMs = now - d.getTime();
                        const diffMin = Math.floor(diffMs / 60000);
                        const diffHr = Math.floor(diffMs / 3600000);
                        const diffDay = Math.floor(diffMs / 86400000);
                        if (diffMin < 5) return '🟢 Online';
                        if (diffMin < 60) return diffMin + 'm ago';
                        if (diffHr < 24) return diffHr + 'h ago';
                        if (diffDay < 7) return diffDay + 'd ago';
                        return d.toLocaleDateString();
                      })() : 'Never'}
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {filter !== 'active' && (
                          <button
                            disabled={working[u.id]}
                            onClick={() => updateStatus(u.id, 'active')}
                            style={{
                              padding: '6px 14px', borderRadius: 6, border: 'none',
                              background: '#16a34a', color: '#fff',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              opacity: working[u.id] ? .6 : 1,
                            }}
                          >
                            {working[u.id] ? '…' : '✓ Approve'}
                          </button>
                        )}
                        {filter !== 'rejected' && (
                          <button
                            disabled={working[u.id]}
                            onClick={() => updateStatus(u.id, 'rejected')}
                            style={{
                              padding: '6px 14px', borderRadius: 6, border: 'none',
                              background: '#dc2626', color: '#fff',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              opacity: working[u.id] ? .6 : 1,
                            }}
                          >
                            {working[u.id] ? '…' : '✕ Reject'}
                          </button>
                        )}
                        {filter === 'rejected' && (
                          <button
                            disabled={working[u.id]}
                            onClick={() => updateStatus(u.id, 'active')}
                            style={{
                              padding: '6px 14px', borderRadius: 6, border: 'none',
                              background: '#0f1f3d', color: '#fff',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Reinstate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        }

        {/* ── Feedback Tab ─────────────────────────────────────────── */}
        {activeTab === 'feedback' && (
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,.4)', overflow: 'hidden' }}>
            {/* Header + filters */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f1f3d' }}>Analyst Feedback</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{feedback.length} submission{feedback.length !== 1 ? 's' : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Filter:</span>
                {['all','5','4','3','2','1'].map(f => (
                  <button key={f} onClick={() => setFeedbackFilter(f)} style={{
                    padding: '4px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: feedbackFilter === f ? '#0f1f3d' : '#f8fafc',
                    color: feedbackFilter === f ? '#fff' : '#64748b',
                    border: '1px solid ' + (feedbackFilter === f ? '#0f1f3d' : '#e2e8f0'),
                  }}>
                    {f === 'all' ? 'All' : '★'.repeat(parseInt(f))}
                  </button>
                ))}
                <button onClick={loadFeedback} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b' }}>
                  ↺ Refresh
                </button>
              </div>
            </div>

            {feedbackLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading feedback…</div>
            ) : feedback.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>No feedback yet</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Feedback appears here after analysts close out their first contract.</div>
              </div>
            ) : (
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {feedback
                  .filter(f => feedbackFilter === 'all' || String(f.rating) === feedbackFilter)
                  .map(f => (
                    <div key={f.id} style={{ padding: '16px 20px', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fafafa' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f1f3d' }}>{f.analyst_name || 'Unknown Analyst'}</div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                            {f.contract_title || 'Unknown Contract'} · {f.created_at ? new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 18, color: '#F59E0B' }}>{'★'.repeat(f.rating || 0)}</span>
                          <span style={{ fontSize: 18, color: '#e2e8f0' }}>{'★'.repeat(5 - (f.rating || 0))}</span>
                          {f.celebration_type && (
                            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: '#f1f5f9', color: '#94a3b8', marginLeft: 4 }}>{f.celebration_type}</span>
                          )}
                        </div>
                      </div>
                      {f.went_well && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#16a34a', marginBottom: 3 }}>✓ What worked well</div>
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{f.went_well}</div>
                        </div>
                      )}
                      {f.could_improve && (
                        <div>
                          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#dc2626', marginBottom: 3 }}>⚑ Could be better</div>
                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{f.could_improve}</div>
                        </div>
                      )}
                      {!f.went_well && !f.could_improve && (
                        <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No written feedback — rating only</div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* ── Signup Registry Tab (static CA_AGENCIES list for the signup dropdown) ── */}
        {activeTab === 'agencies' && (
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,.4)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f1f3d' }}>Signup Registry</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Pre-loaded CA state agencies available in the signup dropdown. This is the menu new users pick from — not the list of agencies actively using GoldenScope (see the Agencies tab for those).</div>
            </div>

            {/* Add custom agency */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1f3d', marginBottom: 12 }}>Add Agency to List</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>AGENCY NAME</div>
                  <input value={newAgencyName} onChange={e => setNewAgencyName(e.target.value)}
                    placeholder="Full agency name" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>SCO CODE</div>
                  <input value={newAgencyCode} onChange={e => setNewAgencyCode(e.target.value)}
                    placeholder="e.g. 3355" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>ABBREVIATION</div>
                  <input value={newAgencyAbbr} onChange={e => setNewAgencyAbbr(e.target.value)}
                    placeholder="e.g. DGS" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>CATEGORY</div>
                  <input value={newAgencyCategory} onChange={e => setNewAgencyCategory(e.target.value)}
                    placeholder="e.g. Energy" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <button
                  onClick={() => {
                    if (!newAgencyName.trim() || !newAgencyCode.trim()) return alert('Name and code required');
                    setCustomAgencies(a => [...a, { name: newAgencyName, code: newAgencyCode, abbr: newAgencyAbbr, category: newAgencyCategory || 'Other' }]);
                    setNewAgencyName(''); setNewAgencyCode(''); setNewAgencyAbbr(''); setNewAgencyCategory('');
                    alert('Agency added — it will appear in the signup dropdown on next page load.');
                  }}
                  style={{ padding: '7px 16px', background: '#0f1f3d', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  + Add
                </button>
              </div>
            </div>

            {/* Agency list */}
            <div style={{ padding: '0 0 16px', maxHeight: 480, overflowY: 'auto' }}>
              {Object.entries(
                [...CA_AGENCIES, ...customAgencies].reduce((acc, a) => {
                  if (!acc[a.category]) acc[a.category] = [];
                  acc[a.category].push(a);
                  return acc;
                }, {})
              ).map(([category, agencies]) => (
                <div key={category}>
                  <div style={{ padding: '10px 24px 4px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#94a3b8', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    {category}
                  </div>
                  {agencies.map(a => (
                    <div key={a.name} style={{ display: 'flex', alignItems: 'center', padding: '9px 24px', borderBottom: '1px solid #f8fafc', gap: 12 }}>
                      <div style={{ flex: 1, fontSize: 13, color: '#0f1f3d', fontWeight: 500 }}>{a.name}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{a.code}</div>
                      <div style={{ fontSize: 11, padding: '2px 8px', background: '#f1f5f9', borderRadius: 99, color: '#64748b' }}>{a.abbr}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Agencies Tab (real registered agencies — jurisdiction, modules, doc profile) ── */}
        {activeTab === 'modules' && (
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,.4)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f1f3d' }}>🏛 Agencies</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Agencies actively using GoldenScope. Set each one's jurisdiction, modules, and document profile.</div>
              </div>
            </div>

            {agenciesLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading agencies...</div>
            ) : registeredAgencies.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🏛</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>No registered agencies yet</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Agencies appear here once users sign up and are approved.</div>
              </div>
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                {registeredAgencies.map(agency => {
                  const isEditing = editingModules === agency.id;
                  const cfg = isEditing ? moduleConfig : (agency.module_config || {});
                  const enabledCount = Object.values(cfg).filter(Boolean).length;
                  const totalCount = Object.keys(MODULE_DEFS).length;

                  return (
                    <div key={agency.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {/* Agency row header */}
                      <div style={{ display: 'flex', alignItems: 'center', padding: '14px 24px', gap: 12, background: isEditing ? '#fffbeb' : '#fff' }}>
                        {agency.logo_url ? (
                          <img src={agency.logo_url} alt={agency.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', border: '1px solid #e2e8f0', background: '#fff', padding: 3, flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#0f1f3d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#c49922', flexShrink: 0 }}>
                            {(agency.name || 'A')[0].toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1f3d' }}>{agency.name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {agency.division ? agency.division + ' · ' : ''}
                            {enabledCount}/{totalCount} modules enabled
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Jurisdiction:</span>
                            {jurisdictionLoading ? (
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>loading…</span>
                            ) : (
                              <select
                                value={agency.jurisdiction_node_id || ''}
                                onChange={e => changeAgencyJurisdiction(agency.id, e.target.value)}
                                style={{
                                  fontSize: 11, padding: '3px 7px', borderRadius: 6,
                                  border: '1px solid ' + (agency.jurisdiction_node_id ? '#cbd5e1' : '#fca5a5'),
                                  background: agency.jurisdiction_node_id ? '#fff' : '#fef2f2',
                                  color: '#0f1f3d', cursor: 'pointer', maxWidth: 300,
                                }}
                              >
                                {!agency.jurisdiction_node_id && <option value="">— not assigned —</option>}
                                {jurisdictionNodes.map(n => (
                                  <option key={n.id} value={n.id}>{nodeIndentLabel(n)}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                        {!isEditing ? (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {/* Logo upload */}
                            <input type="file" accept="image/*" style={{ display: 'none' }}
                              ref={el => logoRefs.current[agency.id] = el}
                              onChange={e => e.target.files[0] && uploadAgencyLogo(agency.id, e.target.files[0])} />
                            {agency.logo_url ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <img src={agency.logo_url} alt="logo" style={{ height: 28, maxWidth: 80, objectFit: 'contain', borderRadius: 4, border: '1px solid #e2e8f0', background: '#fff', padding: 2 }} />
                                <button onClick={() => removeAgencyLogo(agency.id)}
                                  title="Remove logo"
                                  style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 5, color: '#dc2626', fontSize: 10, padding: '2px 6px', cursor: 'pointer' }}>✕</button>
                              </div>
                            ) : (
                              <button onClick={() => logoRefs.current[agency.id]?.click()}
                                disabled={uploadingLogo === agency.id}
                                style={{ padding: '5px 10px', borderRadius: 7, border: '1px dashed #cbd5e1', background: '#f8fafc', fontSize: 11, cursor: 'pointer', color: '#64748b' }}>
                                {uploadingLogo === agency.id ? '⏳' : '🖼 Logo'}
                              </button>
                            )}
                            <button
                              onClick={() => { setEditingModules(agency.id); setModuleConfig(agency.module_config || getDefaultModuleConfig()); }}
                              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#0f1f3d' }}>
                              ⚙ Configure
                            </button>
                            <button
                              onClick={() => openDocTemplateEditor(agency)}
                              title={agency.doc_template ? 'Edit agency doc template profile' : 'Set agency doc template profile'}
                              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid ' + (agency.doc_template ? '#16a34a' : '#e2e8f0'), background: agency.doc_template ? '#f0fdf4' : '#f8fafc', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: agency.doc_template ? '#16a34a' : '#0f1f3d' }}>
                              📝 Doc Profile {agency.doc_template ? '✓' : ''}
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setEditingModules(null)}
                              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, cursor: 'pointer', color: '#64748b' }}>
                              Cancel
                            </button>
                            <button onClick={() => saveModuleConfig(agency.id)} disabled={savingModules}
                              style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#0f1f3d' }}>
                              {savingModules ? 'Saving...' : '💾 Save'}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Module toggles (expanded when editing) */}
                      {isEditing && (
                        <div style={{ padding: '0 24px 20px', background: '#fffbeb' }}>
                          {/* Quick actions */}
                          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                            <button onClick={() => setModuleConfig(Object.fromEntries(Object.keys(MODULE_DEFS).map(k => [k, true])))}
                              style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontWeight: 600 }}>
                              ✓ Enable All
                            </button>
                            <button onClick={() => setModuleConfig(Object.fromEntries(Object.keys(MODULE_DEFS).map(k => [k, false])))}
                              style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
                              ✗ Disable All
                            </button>
                            {Object.entries(PRESET_CONFIGS).map(([key, preset]) => (
                              <button key={key} onClick={() => setModuleConfig({ ...getDefaultModuleConfig(), ...preset.config })}
                                style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', cursor: 'pointer', fontWeight: 600 }}>
                                {preset.label}
                              </button>
                            ))}
                          </div>

                          {/* Module groups */}
                          {Object.entries(MODULE_GROUPS).map(([groupKey, group]) => (
                            <div key={groupKey} style={{ marginBottom: 16 }}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#94a3b8', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid #f1f5f9' }}>
                                {group.label}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                                {group.modules.map(modKey => {
                                  const mod = MODULE_DEFS[modKey];
                                  if (!mod) return null;
                                  const enabled = cfg[modKey] !== false;
                                  return (
                                    <label key={modKey} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: '1.5px solid ' + (enabled ? '#c49922' : '#e2e8f0'), background: enabled ? '#fffbeb' : '#f8fafc', cursor: 'pointer' }}>
                                      <div style={{ position: 'relative', width: 36, height: 20, flexShrink: 0 }}>
                                        <input type="checkbox" checked={enabled}
                                          onChange={e => setModuleConfig(p => ({ ...p, [modKey]: e.target.checked }))}
                                          style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer', width: '100%', height: '100%', margin: 0 }} />
                                        <div style={{ width: 36, height: 20, borderRadius: 10, background: enabled ? '#c49922' : '#e2e8f0', transition: 'background .2s', position: 'absolute', top: 0, left: 0 }} />
                                        <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: enabled ? 18 : 2, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                                      </div>
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0f1f3d' }}>{mod.icon} {mod.label}</div>
                                        <div style={{ fontSize: 10, color: '#94a3b8' }}>{mod.desc}</div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Doc-template profile editor (expanded when editingDocTemplate matches) */}
                      {editingDocTemplate === agency.id && (
                        <div style={{ padding: '0 24px 20px', background: '#f0fdf4', borderTop: '1px solid #d1fae5' }}>
                          <div style={{ paddingTop: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1f3d', marginBottom: 4 }}>📝 Document Template Profile</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                              JSON profile that defines this agency's default formatting (font, headers, exhibit titles, table colors, SOW section structure). Used as the fallback when no specific Contract Template Library entry matches at draft time. Leave empty to use the app default (Energy Safety pattern).
                            </div>
                            <textarea
                              value={docTemplateDraft}
                              onChange={e => setDocTemplateDraft(e.target.value)}
                              spellCheck={false}
                              rows={18}
                              style={{
                                width: '100%', fontFamily: 'monospace', fontSize: 11.5,
                                padding: 12, borderRadius: 8, border: '1.5px solid #d1fae5',
                                background: '#fff', color: '#0f1f3d', resize: 'vertical',
                              }}
                              placeholder='{
  "font": { "family": "Arial", "body_size_pt": 12 },
  "drafting_voice": { "agency_name_in_text": "Your Agency" }
}'
                            />
                            {docTemplateError && (
                              <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6, fontWeight: 600 }}>
                                ⚠ {docTemplateError}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                              <button onClick={() => saveDocTemplate(agency.id)} disabled={savingDocTemplate}
                                style={{ padding: '6px 16px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#fff' }}>
                                {savingDocTemplate ? 'Saving…' : '💾 Save Profile'}
                              </button>
                              <button onClick={closeDocTemplateEditor} disabled={savingDocTemplate}
                                style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, cursor: 'pointer', color: '#64748b' }}>
                                Cancel
                              </button>
                              {agency.doc_template && (
                                <button onClick={() => { setDocTemplateDraft(''); }}
                                  style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', fontSize: 12, cursor: 'pointer', color: '#dc2626' }}>
                                  Clear (revert to app default)
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── AI Usage Tab ─────────────────────────────────────────── */}
        {activeTab === 'ai_usage' && (() => {
          // Aggregate by tag
          const byTag = {};
          const byModel = {};
          let totalCost = 0, totalCalls = 0, totalIn = 0, totalOut = 0, totalErrors = 0;
          for (const r of aiUsage) {
            const t = r.tag || 'untagged';
            const m = r.model || 'unknown';
            if (!byTag[t]) byTag[t] = { calls: 0, cost: 0, in: 0, out: 0, errors: 0 };
            if (!byModel[m]) byModel[m] = { calls: 0, cost: 0 };
            byTag[t].calls += 1;
            byTag[t].cost += Number(r.cost_usd) || 0;
            byTag[t].in += r.input_tokens || 0;
            byTag[t].out += r.output_tokens || 0;
            if (r.error) byTag[t].errors += 1;
            byModel[m].calls += 1;
            byModel[m].cost += Number(r.cost_usd) || 0;
            totalCost += Number(r.cost_usd) || 0;
            totalCalls += 1;
            totalIn += r.input_tokens || 0;
            totalOut += r.output_tokens || 0;
            if (r.error) totalErrors += 1;
          }
          const tagRows = Object.entries(byTag).sort((a, b) => b[1].cost - a[1].cost);
          const modelRows = Object.entries(byModel).sort((a, b) => b[1].cost - a[1].cost);
          const fmtMoney = (n) => n < 0.01 ? '<$0.01' : '$' + n.toFixed(2);
          const fmtTokens = (n) => n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);

          return (
            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,.4)', overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f1f3d' }}>✦ AI Usage</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Anthropic API spend and call patterns, by feature. Data updated in real time.</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[7, 30].map(d => (
                    <button key={d} onClick={() => setAiUsageDays(d)} style={{
                      padding: '6px 14px', borderRadius: 8, border: 'none',
                      background: aiUsageDays === d ? '#0f1f3d' : '#f1f5f9',
                      color: aiUsageDays === d ? '#fff' : '#475569',
                      fontWeight: aiUsageDays === d ? 700 : 400,
                      cursor: 'pointer', fontSize: 12,
                    }}>{d} days</button>
                  ))}
                </div>
              </div>

              {aiUsageLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading AI usage data...</div>
              ) : aiUsage.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>✦</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>No AI calls logged in the last {aiUsageDays} days</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Usage will appear here as Penny features are used.</div>
                </div>
              ) : (
                <div style={{ padding: 24 }}>
                  {/* Summary tiles */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                    {[
                      { label: 'Total spend', value: fmtMoney(totalCost), color: '#0f1f3d' },
                      { label: 'Total calls', value: totalCalls.toLocaleString(), color: '#0f1f3d' },
                      { label: 'Tokens (in / out)', value: fmtTokens(totalIn) + ' / ' + fmtTokens(totalOut), color: '#0f1f3d' },
                      { label: 'Errors', value: totalErrors, color: totalErrors > 0 ? '#dc2626' : '#0f1f3d' },
                    ].map((t, i) => (
                      <div key={i} style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{t.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: t.color }}>{t.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* By model */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1f3d', marginBottom: 10 }}>By model</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                      {modelRows.map(([m, s]) => (
                        <div key={m} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#475569' }}>{m}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{s.calls} calls</div>
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f1f3d' }}>{fmtMoney(s.cost)}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By tag (feature) */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1f3d', marginBottom: 10 }}>By feature (tag)</div>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '10px 14px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                        <div>Tag</div>
                        <div style={{ textAlign: 'right' }}>Calls</div>
                        <div style={{ textAlign: 'right' }}>Tokens in</div>
                        <div style={{ textAlign: 'right' }}>Tokens out</div>
                        <div style={{ textAlign: 'right' }}>Cost</div>
                      </div>
                      {tagRows.map(([t, s], i) => (
                        <div key={t} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '10px 14px', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', fontSize: 13, alignItems: 'center' }}>
                          <div style={{ fontFamily: 'ui-monospace, monospace', color: t === 'untagged' ? '#94a3b8' : '#0f1f3d', fontSize: 12 }}>
                            {t}
                            {s.errors > 0 && <span style={{ marginLeft: 8, fontSize: 10, color: '#dc2626', fontWeight: 700 }}>⚠ {s.errors} err</span>}
                          </div>
                          <div style={{ textAlign: 'right', color: '#475569' }}>{s.calls.toLocaleString()}</div>
                          <div style={{ textAlign: 'right', color: '#94a3b8', fontSize: 12 }}>{fmtTokens(s.in)}</div>
                          <div style={{ textAlign: 'right', color: '#94a3b8', fontSize: 12 }}>{fmtTokens(s.out)}</div>
                          <div style={{ textAlign: 'right', fontWeight: 700, color: '#0f1f3d' }}>{fmtMoney(s.cost)}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginTop: 16, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                    Showing {aiUsage.length.toLocaleString()} calls from the last {aiUsageDays} days.
                    {aiUsage.length >= 5000 && ' Capped at 5,000 — extend the range to query older data.'}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        <div style={{ textAlign: 'center', marginTop: 20, color: 'rgba(255,255,255,.2)', fontSize: 12 }}>
          GoldenScope Super Admin · Changes take effect immediately
        </div>
      </div>
    </div>
  );
}
